import React from 'react';
import { useObjectRef } from 'react-aria';
import { SwitchButton, SwitchField } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { FieldMessage, resolveFieldState, useFieldMessageIds } from '../field/field-shell';

/**
 * ComplyKit Switch: React Aria Components (SwitchField + SwitchButton)
 * + Tailwind v4 + tailwind-variants.
 * Props are fully abstracted from the underlying library. A switch is an
 * immediate-effect control, so it deliberately has no `isRequired` and no
 * `error`: a setting that applies on toggle is never left to fix (field.md).
 * It takes a description, on a field's message line, for what the setting
 * does or why it is unavailable.
 *
 * The label text renders INSIDE the SwitchButton (the pressed <label>
 * wrapping the input), so clicking the track or the text toggles and the
 * text is the accessible name. See the checkbox for the note about
 * automation-dispatched (virtual) pointer events.
 */
interface SwitchCommonProps {
  /**
   * Help under the label: what the setting does, or why it is unavailable.
   * Inline content only.
   */
  description?: React.ReactNode;
  /** Whether the switch is on (controlled). */
  isSelected?: boolean;
  /** Whether the switch is on at mount (uncontrolled). */
  defaultSelected?: boolean;
  /** Called with the new state on every change. */
  onChange?: (isSelected: boolean) => void;
  /** Called when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Whether the input takes focus on mount. */
  autoFocus?: boolean;
  /**
   * Washed out and skipped by Tab. Prefer `isReadOnly` whenever the value
   * still matters to the reader.
   */
  isDisabled?: boolean;
  /**
   * Focusable but not changeable, the track in neutral. Say why in
   * `description`. Not for a save in flight: that is `isPending`.
   */
  isReadOnly?: boolean;
  /**
   * The switch's own save is in flight: the value and tone stay, a spinner
   * turns in the thumb, presses are ignored and focus stays, as a pending
   * Button's does.
   */
  isPending?: boolean;
  /** The value of the switch input (used for form submission). */
  value?: string;
  /** The form field name. */
  name?: string;
  /** The id of the input element. */
  id?: string;
  /**
   * Removes the switch input from the page tab order. For switches that are
   * purely visual state indicators inside another interactive control, which
   * would otherwise contain a second, nested tab stop. Concrete case: the
   * dark mode switch inside the header account menu, where the menu item
   * itself handles click and keyboard and the switch only displays the state.
   */
  excludeFromTabOrder?: boolean;
  /**
   * Visual emphasis of the on state (the off state is identical across
   * variants). From the canonical ComplyKit variant vocabulary.
   */
  variant?: 'solid' | 'soft' | 'outline';
  /**
   * Recolours the control onto a semantic scale: `critical` for a choice
   * that destroys, `ai` for one the agent made. Absent means the brand
   * accent.
   */
  tone?: 'critical' | 'ai';
  /** Size of the switch. */
  size?: Size;
}

/* A switch must have a name, held by the type as Checkbox's is. */
interface SwitchLabelledProps extends SwitchCommonProps {
  /** The visible label; renders inside the pressed label element, so it is
   * also the accessible name. */
  children: React.ReactNode;
  /** Override for the rare switch whose visible label needs rephrasing for
   * screen readers. */
  'aria-label'?: string;
  'aria-labelledby'?: never;
}

interface SwitchUnlabelledProps extends SwitchCommonProps {
  children?: never;
  /** The accessible name of a switch with no visible label (a table row). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface SwitchLabelledByProps extends SwitchCommonProps {
  children?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the switch (a setting's title). */
  'aria-labelledby': string;
}

export type SwitchProps = SwitchLabelledProps | SwitchUnlabelledProps | SwitchLabelledByProps;

const switchStyles = tv({
  slots: {
    root: ['ck:group ck:font-sans ck:text-text-default', 'ck:select-none'],
    // The button is a group too: react-aria places data-focus-visible (and
    // press states) on the pressed label, not on the field root. It washes
    // out alone when disabled, so the description stays legible.
    button: [
      'ck:group ck:flex ck:items-center ck:gap-2',
      'ck:group-disabled:cursor-not-allowed ck:group-disabled:opacity-50',
    ],
    // Inset past the track and its gap, so the message starts under the label.
    message: 'ck:mt-1',
    track: [
      'ck:flex ck:shrink-0 ck:items-center ck:p-inset',
      // Filled tracks are borderless across Chakra, iOS, shadcn, Radix, and
      // HeroUI (Material is the outlier); the transparent border keeps the
      // box model so the thumb travel math is unchanged.
      'ck:rounded-6 ck:border ck:border-transparent ck:bg-neutral-a-5',
      'ck:transition-chrome',
      'ck:group-focus-visible:focus-ring',
    ],
    thumb: [
      'ck:flex ck:items-center ck:justify-center ck:rounded-6 ck:bg-white ck:shadow-sm',
      'ck:text-neutral-9 ck:group-selected:text-tone',
      'ck:transition-transform',
    ],
    spinner: 'ck:animate-spin ck:rounded-full ck:border-current ck:border-t-transparent',
  },
  variants: {
    /* Labels on the field ramp (12/14/16, typography.md), as Checkbox's. */
    size: {
      sm: {
        root: 'ck:text-xs',
        track: 'ck:h-4 ck:w-7',
        thumb: 'ck:size-3',
        spinner: 'ck:size-2 ck:border',
        message: 'ck:switch-inset-sm',
      },
      md: {
        root: 'ck:text-sm',
        track: 'ck:h-5 ck:w-9',
        thumb: 'ck:size-4',
        spinner: 'ck:size-2.5 ck:border-2',
        message: 'ck:switch-inset-md',
      },
      lg: {
        root: 'ck:text-base',
        track: 'ck:h-6 ck:w-11',
        thumb: 'ck:size-5',
        spinner: 'ck:size-3 ck:border-2',
        message: 'ck:switch-inset-lg',
      },
    },
    /* Read-only and pending drop the press cues; only read-only turns the
     * tone neutral (data-tone). */
    inert: {
      false: { button: 'ck:cursor-pointer', track: 'ck:group-hover:bg-neutral-a-6' },
      true: { button: 'ck:cursor-default' },
    },
    variant: {
      solid: {
        track: 'ck:group-selected:bg-fill',
      },
      soft: {
        track: 'ck:group-selected:bg-tone-a-4',
        thumb: 'ck:group-selected:bg-tone ck:group-selected:text-tone-contrast',
      },
      outline: {
        // The one bordered track by design: the outline carries the shape and
        // the thumb carries the state, so the track fill stays transparent.
        track: [
          'ck:border-border-default ck:bg-transparent',
          'ck:group-selected:border-tone-a-8 ck:group-selected:bg-transparent',
        ],
        thumb: [
          'ck:bg-neutral-a-9 ck:shadow-none ck:group-selected:bg-tone',
          'ck:text-white ck:group-selected:text-tone-contrast',
        ],
      },
    },
    selected: {
      true: {},
    },
  },
  compoundVariants: [
    {
      variant: 'solid',
      inert: false,
      class: { track: 'ck:group-hover:group-selected:bg-fill-hover' },
    },
    {
      variant: 'soft',
      inert: false,
      class: { track: 'ck:group-hover:group-selected:bg-tone-a-5' },
    },
    {
      variant: 'outline',
      inert: false,
      class: {
        track: [
          'ck:group-hover:border-border-active ck:group-hover:bg-transparent',
          'ck:group-hover:group-selected:border-tone-a-9',
        ],
      },
    },
    { size: 'sm', selected: true, class: { thumb: 'ck:translate-x-2.5' } },
    { size: 'md', selected: true, class: { thumb: 'ck:translate-x-3.5' } },
    { size: 'lg', selected: true, class: { thumb: 'ck:translate-x-4.5' } },
  ],
});

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  {
    children,
    description,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    isPending = false,
    onBlur,
    onFocus,
    size = 'md',
    variant = 'solid',
    tone,
    ...props
  },
  ref,
) {
  const inputRef = useObjectRef(ref);
  const { isDisabled, isReadOnly } = resolveFieldState({
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const inert = isReadOnly || isPending;
  const { root, button, track, thumb, spinner, message } = switchStyles({
    size,
    variant,
    inert,
  });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, undefined);

  return (
    <SwitchField
      {...props}
      inputRef={inputRef}
      onBlur={onBlur as ((e: React.FocusEvent<Element>) => void) | undefined}
      onFocus={onFocus as ((e: React.FocusEvent<Element>) => void) | undefined}
      isDisabled={isDisabled}
      isReadOnly={inert}
      aria-describedby={describedBy}
      className={root()}
      data-component="switch"
      data-tone={isReadOnly ? 'neutral' : (tone ?? 'accent')}
      data-pending={isPending || undefined}
    >
      <SwitchButton className={button()}>
        {({ isSelected }) => (
          <>
            <span className={track()} aria-hidden="true">
              <span className={thumb({ size, variant, inert, selected: isSelected })}>
                {isPending ? <span className={spinner()} /> : null}
              </span>
            </span>
            {children}
          </>
        )}
      </SwitchButton>
      {description ? (
        <div className={message()}>
          <FieldMessage description={description} descriptionId={descriptionId} errorId={errorId} />
        </div>
      ) : null}
    </SwitchField>
  );
});
